import { useMemo, useCallback, useEffect, useReducer, useRef } from 'react';
import { get, isFunction, isNil } from 'lodash/fp';
import classnames from 'classnames/bind';
import { Defaults } from 'app/lib/constants';
import { useResizeObserver } from 'app/lib/hooks';
import { useTheme } from 'app/components/core/ThemeProvider';
import { Head } from './Head';
import { Body, type BodyProps as BodyProps } from './Body';
import * as styles from './Table.css';
import type { TableColumn } from './types';

const cx = classnames.bind(styles);

export enum SortDirection {
    ASC = 1,
    DESC = -1
}

export enum TableVariant {
    STANDARD = 'standard',
    COMPACT = 'compact'
}

interface SortState {
    sortColumn: string | undefined;
    sortDirection: SortDirection;
}

type SortAction = { type: 'ClickHeader'; column: string };

const INITIAL_SORT_STATE = { sortColumn: undefined, sortDirection: SortDirection.ASC };
const getInitialSortState = (sortColumn: string | undefined, sortDirection: SortDirection) => ({
    sortDirection,
    sortColumn
});

function sortByField<TRow>(a: TRow, b: TRow, field: keyof TRow, direction: SortDirection) {
    const fa = get(field, a);
    const fb = get(field, b);
    return (fa < fb ? -1 : fa > fb ? 1 : 0) * direction;
}

export interface TableProps<TRow> {
    testId?: string;
    className?: string;
    rowClassName?: BodyProps<TRow>['rowClassName'];
    variant?: TableVariant;

    /** The columns configuration object. Columns are rendered in order they appear */
    columns: TableColumn<TRow>[];

    /** List of rows to be rendered, with each row represented as a data object */
    data: TRow[];

    menuRenderer?: BodyProps<TRow>['menuRenderer'];

    /** Field name to serve as the unique key for the rows */
    rowKey: keyof TRow;

    /**
     * A field in the row object that contains additional details. If present,
     * this will treat the row as a disclosure widget and render the content with
     * the provided `detailsRenderer`.
     */
    detailsKey?: keyof TRow;
    detailsRenderer?: BodyProps<TRow>['detailsRenderer'];

    /** If the details row is expanded, the data row is sticky to the top of the table */
    stickyIfExpanded?: boolean;

    /** The `rowKey` value of the selected row */
    selectedRow?: string | number;
    onSelectRow?: BodyProps<TRow>['onSelectRow'];

    sortBy?: string;
    sortDir?: SortDirection;

    /** Sort the data on the backend */
    onSort?: (column: string, direction: SortDirection) => void;

    isLoadingData?: boolean;
    onLoadMoreData?: () => void;

    /** Percentage of scrollable height. Adjust this value based on your design */
    threshold?: number;
}

export function Table<TRow extends Record<string, unknown>>({
    testId,
    className,
    rowClassName,
    variant = TableVariant.STANDARD,
    columns,
    data = Defaults.ARRAY,
    rowKey,
    detailsKey,
    detailsRenderer,
    stickyIfExpanded = false,
    selectedRow,
    onSelectRow,
    sortBy,
    sortDir = SortDirection.ASC,
    onSort,
    menuRenderer,
    isLoadingData = false,
    onLoadMoreData,
    threshold = 0.8
}: TableProps<TRow>) {
    const sortReducer = (state: SortState, action: SortAction): SortState => {
        switch (action.type) {
            case 'ClickHeader': {
                const isSameColumn = state.sortColumn === action.column;
                const sortDirection = isSameColumn ? state.sortDirection * -1 : 1;

                // Trigger a backend request for sorting
                if (isFunction(onSort)) {
                    onSort(action.column, sortDirection);
                }

                return {
                    sortColumn: action.column,
                    sortDirection
                };
            }
            default:
                return INITIAL_SORT_STATE;
        }
    };

    const [{ sortColumn, sortDirection }, dispatch] = useReducer(
        sortReducer,
        getInitialSortState(sortBy, sortDir)
    );

    const { themeClassname } = useTheme();
    const ref = useRef<HTMLDivElement>(null);
    const { width } = useResizeObserver(ref);
    const classes = cx('base', variant, themeClassname, className);

    const handleScroll = useCallback(() => {
        if (ref.current && !isLoadingData) {
            const { scrollTop, scrollHeight, clientHeight } = ref.current;
            const scrollableHeight = scrollHeight - clientHeight;
            const scrollPercentage = scrollTop / scrollableHeight;

            if (scrollPercentage > threshold && isFunction(onLoadMoreData)) {
                onLoadMoreData();
            }
        }
    }, [onLoadMoreData, threshold, isLoadingData]);

    useEffect(() => {
        if (onLoadMoreData) {
            const scrollContainer = ref.current;

            scrollContainer?.addEventListener('scroll', handleScroll);

            return () => {
                scrollContainer?.removeEventListener('scroll', handleScroll);
            };
        }
    }, [onLoadMoreData, handleScroll]);

    const sortedData = useMemo(() => {
        if (isNil(sortColumn) || onSort) {
            return data;
        }

        return [...data].sort(
            (a, b) =>
                sortByField(a, b, sortColumn, sortDirection) ||
                (!isNil(sortBy) ? sortByField(a, b, sortBy, 1) : 0)
        );
    }, [data, sortBy, sortColumn, sortDirection, onSort]);

    const handleHeaderCellClick = (column: string) => dispatch({ type: 'ClickHeader', column });

    return (
        <div className={classes} ref={ref}>
            {data?.length > 0 && (
                <table data-testid={testId}>
                    <Head
                        columns={columns}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        hasMenu={isFunction(menuRenderer)}
                        hasDetails={isFunction(detailsRenderer)}
                        onClickHeader={handleHeaderCellClick}
                    />
                    <Body
                        columns={columns}
                        rows={sortedData}
                        rowKey={rowKey}
                        rowClassName={rowClassName}
                        selectedRow={selectedRow}
                        onSelectRow={onSelectRow}
                        menuRenderer={menuRenderer}
                        detailsKey={detailsKey}
                        detailsRenderer={detailsRenderer}
                        stickyIfExpanded={stickyIfExpanded}
                        width={width}
                    />
                </table>
            )}
        </div>
    );
}
